import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "../app/SessionContext";
import type {
  ClassifyInput,
  CreateWorkOrderInput,
  ContractorAction,
  ReceiptInput,
  SetSlaInput,
  WorkSettings,
  TransitionInput,
} from "../domain/workOrders";
import type { ContactOrg, ContactPerson, NotifyRule, Recipient } from "../domain/contacts";
import type { CallPolicy, DndState, MeetingDraft } from "../domain/calls";
import type { Movement } from "../domain/aodb";
import type { Policy, Resource, Rule, WhatIf } from "../domain/allocation";
import type { ActualKey, NewMovement } from "../domain/flightEntries";
import type { Checkpoint, LanePlan, Observation } from "../domain/flow";
import type { FeedDraft, FeedOutcome } from "../domain/flightFeeds";
import type { NewNoteInput } from "../domain/estate";
import type { HrEmployeeInput, HrEmployeePatch, HrRulesPatch } from "../domain/hr";
import type { HrAttendanceInput, HrRecordInput, RecordStatus } from "../domain/hrFile";
import type { HrClockInput, HrGeofence, HrSelfRequest } from "../domain/hrClock";
import type { ActionKind, NewTestInput } from "../domain/testing";
import type { NewMeterInput } from "../domain/meters";
import type { AlertKind } from "../domain/alerts";
import type { NewDocumentInput, NewRevisionInput, RevisionTransitionInput } from "../domain/library";
import type { VerifyRuleInput } from "../domain/compliance";
import type {
  CloseActionInput,
  CreateProjectInput,
  GateDecisionInput,
  GateSubmitInput,
  ItemResultInput,
  NewLogEntryInput,
  ProjectQuery,
  RecordResultInput,
  ScheduleInput,
  UpdateProjectInput,
} from "../domain/projects";
import type { DataSource, DocumentQuery, InspectionQuery, WorkOrderQuery } from "./dataSource";
import type { ClosureQuery, DivergenceQuery, PlanQuery } from "./dataSource";

/**
 * React Query hooks — the only way screens read or write data. Keys are
 * namespaced by site so switching aerodrome never shows the other one's rows
 * while the new ones load.
 */
export const keys = {
  workOrders: (q: WorkOrderQuery) => ["work-orders", q.siteId, q.status ?? ""] as const,
  workOrder: (id: string) => ["work-order", id] as const,
  history: (id: string) => ["work-order-history", id] as const,
  kpis: (siteId: string) => ["kpis", siteId] as const,
  alerts: (siteId: string, kind?: AlertKind) => ["alerts", siteId, kind ?? ""] as const,
  explain: (id: string) => ["alert-explain", id] as const,
  inspections: (q: InspectionQuery) => ["inspections", q.siteId, q.status ?? "", q.template ?? ""] as const,
  inspection: (id: string) => ["inspection", id] as const,
  mapLayers: (siteId: string) => ["map-layers", siteId] as const,
  mapFeatures: (siteId: string, layer: string) => ["map-features", siteId, layer] as const,
  compliance: (siteId: string) => ["compliance", siteId] as const,
  rules: () => ["rules"] as const,
  documents: (q: DocumentQuery) =>
    ["documents", q.siteId, q.siteScope ?? "", q.type ?? "", q.status ?? "", q.tag ?? "", q.q ?? ""] as const,
  document: (id: string) => ["document", id] as const,
  documentTypes: () => ["document-types"] as const,
  notifications: (siteId: string) => ["notifications", siteId] as const,
  projects: (q: ProjectQuery) =>
    ["projects", q.siteId, q.status ?? "", q.category ?? "", q.liveOnly ? "live" : ""] as const,
  project: (id: string) => ["project", id] as const,
  holdPoints: (siteId: string) => ["hold-points", siteId] as const,
  projectAttention: (siteId: string) => ["project-attention", siteId] as const,
  projectInspection: (id: string) => ["project-inspection", id] as const,
  projectVocabulary: () => ["project-vocabulary"] as const,
  projectLog: (id: string) => ["project-log", id] as const,
};

export function useWorkOrders(query: Omit<WorkOrderQuery, "siteId"> = {}, opts: { refetchMs?: number | false } = {}) {
  const { source, site } = useSession();
  const q: WorkOrderQuery = { siteId: site.id, ...query };
  return useQuery({
    queryKey: keys.workOrders(q),
    queryFn: () => source.listWorkOrders(q),
    staleTime: 15_000,
    refetchInterval: opts.refetchMs ?? false,
  });
}

export function useWorkOrder(id: string) {
  const { source } = useSession();
  return useQuery({
    queryKey: keys.workOrder(id),
    queryFn: () => source.getWorkOrder(id),
    /* while a dispatched order waits for the contractor, watch for the receipt */
    refetchInterval: (q) => (q.state.data?.status === "dispatched" && !q.state.data.receivedAt ? 5_000 : false),
  });
}

export function useWorkOrderHistory(id: string) {
  const { source } = useSession();
  return useQuery({ queryKey: keys.history(id), queryFn: () => source.getWorkOrderHistory(id) });
}

export function useKpis() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: keys.kpis(site.id),
    queryFn: () => source.listKpis(site.id),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
}

export function useAlerts(kind?: AlertKind) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: keys.alerts(site.id, kind),
    queryFn: () => source.listAlerts(site.id, kind),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useAlertExplanation(id: string | null) {
  const { source } = useSession();
  return useQuery({
    queryKey: keys.explain(id ?? ""),
    queryFn: () => source.explainAlert(id as string),
    enabled: id != null,
  });
}

export function useInspections(query: Omit<InspectionQuery, "siteId"> = {}) {
  const { source, site } = useSession();
  const q: InspectionQuery = { siteId: site.id, ...query };
  return useQuery({
    queryKey: keys.inspections(q),
    queryFn: () => source.listInspections(q),
    staleTime: 30_000,
  });
}

export function useInspection(id: string) {
  const { source } = useSession();
  return useQuery({ queryKey: keys.inspection(id), queryFn: () => source.getInspection(id) });
}

export function useMapLayers() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: keys.mapLayers(site.id),
    queryFn: () => source.listMapLayers(site.id),
    staleTime: 10 * 60_000,
  });
}

/** One query per visible layer; a drawing changes rarely, so features are kept for the session. */
export function useMapFeatures(layers: readonly string[]) {
  const { source, site } = useSession();
  return useQueries({
    queries: layers.map((layer) => ({
      queryKey: keys.mapFeatures(site.id, layer),
      queryFn: () => source.listMapFeatures(site.id, layer),
      staleTime: Infinity,
    })),
  });
}

/** Detail for several inspections at once — the map needs the failed items' positions. */
export function useInspectionDetails(ids: readonly string[]) {
  const { source } = useSession();
  return useQueries({
    queries: ids.map((id) => ({
      queryKey: keys.inspection(id),
      queryFn: () => source.getInspection(id),
      staleTime: 60_000,
    })),
  });
}

export function useCompliance() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: keys.compliance(site.id),
    queryFn: () => source.getCompliance(site.id),
    staleTime: 60_000,
  });
}

export function useRules() {
  const { source } = useSession();
  return useQuery({ queryKey: keys.rules(), queryFn: () => source.listRules(), staleTime: 5 * 60_000 });
}

export function useVerifyRule() {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: VerifyRuleInput) => source.verifyRule(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.rules() });
      void qc.invalidateQueries({ queryKey: ["alerts"] });
    },
  });
}

export function useDocuments(query: Omit<DocumentQuery, "siteId"> = {}) {
  const { source, site } = useSession();
  const q: DocumentQuery = { siteId: site.id, ...query };
  return useQuery({ queryKey: keys.documents(q), queryFn: () => source.listDocuments(q), staleTime: 60_000 });
}

export function useDocument(id: string) {
  const { source } = useSession();
  return useQuery({ queryKey: keys.document(id), queryFn: () => source.getDocument(id) });
}

/**
 * The planning join. Disabled until every field is present, because the
 * platform refuses an unbounded search and a half-filled form would spend a
 * round trip earning a 422. `enabled` also keeps the answer from changing
 * under the planner while they are still typing the hours.
 */
export function usePlan(query: PlanQuery | null) {
  const { source } = useSession();
  return useQuery({
    queryKey: ["plan", query],
    queryFn: () => source.whenToWork(query as PlanQuery),
    enabled: query != null,
    /* a plan is a question asked at a moment; it is not background data */
    staleTime: 0,
    retry: false,
  });
}

/**
 * The liability curve for a fiscal year. Cached for a while: it is a budget
 * artefact rebuilt from rates and forecasts, not a live reading.
 */
export function useLiabilityCurve(fiscalYear: number) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["liability", "curve", site.id, fiscalYear],
    queryFn: () => source.liabilityCurve(site.id, fiscalYear),
    staleTime: 300_000,
  });
}

export function useLiabilityRegister() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["liability", "register", site.id],
    queryFn: () => source.liabilityRegister(site.id),
    staleTime: 300_000,
  });
}

/** The cost of closing one position over one window. Asked, not polled. */
export function useClosureCost(query: ClosureQuery | null) {
  const { source } = useSession();
  return useQuery({
    queryKey: ["closure", query],
    queryFn: () => source.closureCost(query as ClosureQuery),
    enabled: query != null,
    staleTime: 0,
    retry: false,
  });
}

/** Which positions cannot be closed at all in a window. */
export function useCriticalPositions(from: string, to: string) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["critical", site.id, from, to],
    queryFn: () => source.criticalPositions(site.id, from, to),
    enabled: !!from && !!to,
    staleTime: 60_000,
  });
}

export function useDriftChecks() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["drift", "checks", site.id],
    queryFn: () => source.driftChecks(site.id),
    staleTime: 300_000,
  });
}

export function useDriftSchedule() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["drift", "due", site.id],
    queryFn: () => source.driftSchedule(site.id),
    staleTime: 300_000,
  });
}

export function usePopulations() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["reliability", "populations", site.id],
    queryFn: () => source.populations(site.id),
    staleTime: 300_000,
  });
}

export function useFits(population: string | null) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["reliability", "fits", site.id, population],
    queryFn: () => source.fits(site.id, population as string),
    enabled: !!population,
    staleTime: 300_000,
  });
}

export function useLedgerFacts() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["ledger", "facts", site.id],
    queryFn: () => source.ledgerFacts(site.id),
    staleTime: 120_000,
  });
}

export function useDivergence(query: DivergenceQuery | null) {
  const { source } = useSession();
  return useQuery({
    queryKey: ["ledger", "between", query],
    queryFn: () => source.divergence(query as DivergenceQuery),
    enabled: query != null,
    staleTime: 120_000,
  });
}

export function useCanWork(category: string | null, at: string) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["weather", "can-work", site.id, category, at],
    queryFn: () => source.canWork(site.id, category as string, at),
    enabled: !!category,
    staleTime: 60_000,
  });
}

export function useProgramme(fiscalYear: number, envelope?: number) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["programme", site.id, fiscalYear, envelope],
    queryFn: () => source.programme(site.id, fiscalYear, envelope),
    staleTime: 120_000,
  });
}

export function useCounterfactual(fiscalYear: number, crews?: number) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["counterfactual", site.id, fiscalYear, crews],
    queryFn: () => source.counterfactual(site.id, fiscalYear, crews),
    /* each variant is a full programme run against the database; this is not
       something to refetch on every window focus */
    staleTime: 300_000,
  });
}

export function useProvenance(candidateId: string | null) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["provenance", site.id, candidateId],
    queryFn: () => source.provenance(site.id, candidateId as string),
    enabled: !!candidateId,
    staleTime: 300_000,
  });
}

export function useDocumentTypes() {
  const { source } = useSession();
  return useQuery({ queryKey: keys.documentTypes(), queryFn: () => source.listDocumentTypes(), staleTime: Infinity });
}

export function useNotifications() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: keys.notifications(site.id),
    queryFn: () => source.listNotifications(site.id),
    staleTime: 30_000,
  });
}

export function useTransitionWorkOrder(id: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TransitionInput) => source.transitionWorkOrder(id, input),
    onSuccess: (wo) => {
      qc.setQueryData(keys.workOrder(id), wo);
      void qc.invalidateQueries({ queryKey: ["work-orders"] });
      void qc.invalidateQueries({ queryKey: keys.history(id) });
      void qc.invalidateQueries({ queryKey: ["kpis"] });
      void qc.invalidateQueries({ queryKey: ["alert-explain"] });
      /* a change of state may have told people: the messages list shows it */
      void qc.invalidateQueries({ queryKey: ["work-order-messages", id] });
    },
  });
}

export function useSetWorkOrderSla(id: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SetSlaInput) => source.setWorkOrderSla(id, input),
    onSuccess: (wo) => {
      qc.setQueryData(keys.workOrder(id), wo);
      void qc.invalidateQueries({ queryKey: ["work-orders"] });
      void qc.invalidateQueries({ queryKey: keys.history(id) });
    },
  });
}

export function useAddWorkOrderNote(id: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (text: string) => source.addWorkOrderNote(id, text),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.history(id) }),
  });
}

export function useUploadWorkOrderPhoto(id: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, phase }: { file: File; phase: "before" | "after" }) =>
      source.uploadWorkOrderPhoto(id, file, phase),
    onSuccess: (wo) => {
      qc.setQueryData(keys.workOrder(id), wo);
      void qc.invalidateQueries({ queryKey: ["work-orders"] });
      void qc.invalidateQueries({ queryKey: keys.history(id) });
    },
  });
}

export function useWorkSettings() {
  const { source, site } = useSession();
  return useQuery({ queryKey: ["work-settings", site.id], queryFn: () => source.getWorkSettings(site.id) });
}

export function useSetWorkSettings() {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (s: WorkSettings) => source.setWorkSettings(site.id, s),
    onSuccess: (s) => qc.setQueryData(["work-settings", site.id], s),
  });
}

export function useSimulateContractor(id: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ action, photo, note }: { action: ContractorAction; photo?: File; note?: string }) => {
      if (!source.simulateContractor) throw new Error("only the sample can play the contractor");
      return source.simulateContractor(id, action, photo, note);
    },
    onSuccess: (wo) => {
      qc.setQueryData(keys.workOrder(id), wo);
      void qc.invalidateQueries({ queryKey: ["work-orders"] });
      void qc.invalidateQueries({ queryKey: keys.history(id) });
    },
  });
}

export function useConfirmReceipt(id: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ReceiptInput) => source.confirmWorkOrderReceipt(id, input),
    onSuccess: (wo) => {
      qc.setQueryData(keys.workOrder(id), wo);
      void qc.invalidateQueries({ queryKey: ["work-orders"] });
      void qc.invalidateQueries({ queryKey: keys.history(id) });
      void qc.invalidateQueries({ queryKey: ["work-order-messages", id] });
    },
  });
}

export function useClassifyWorkOrder(id: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ClassifyInput) => source.classifyWorkOrder(id, input),
    onSuccess: (wo) => {
      qc.setQueryData(keys.workOrder(id), wo);
      void qc.invalidateQueries({ queryKey: ["work-orders"] });
      void qc.invalidateQueries({ queryKey: keys.history(id) });
    },
  });
}

export function useCreateWorkOrder() {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<CreateWorkOrderInput, "siteId">) => source.createWorkOrder({ ...input, siteId: site.id }),
    onSuccess: (wo) => {
      qc.setQueryData(keys.workOrder(wo.id), wo);
      void qc.invalidateQueries({ queryKey: ["work-orders"] });
      void qc.invalidateQueries({ queryKey: ["alert-explain"] });
    },
  });
}

/* ---- library writes --------------------------------------------------------------- */

export function useCreateDocument() {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewDocumentInput) => source.createDocument(input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["documents"] }),
  });
}

export function useAddRevision(documentId: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewRevisionInput) => source.addRevision(documentId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.document(documentId) });
      void qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

export function useTransitionRevision(documentId: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { revisionId: string } & RevisionTransitionInput) =>
      source.transitionRevision(v.revisionId, { to: v.to, note: v.note, effectiveFrom: v.effectiveFrom }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.document(documentId) });
      void qc.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}

export function useAcknowledgeRevision(documentId: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (revisionId: string) => source.acknowledgeRevision(revisionId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.document(documentId) }),
  });
}

/* ---- capital projects ------------------------------------------------------------- */

export function useProjects(query: Omit<ProjectQuery, "siteId"> = {}) {
  const { source, site } = useSession();
  const q: ProjectQuery = { siteId: site.id, ...query };
  return useQuery({ queryKey: keys.projects(q), queryFn: () => source.listProjects(q), staleTime: 30_000 });
}

export function useProject(id: string) {
  const { source } = useSession();
  return useQuery({ queryKey: keys.project(id), queryFn: () => source.getProject(id) });
}

/**
 * What is stopping work, refetched on a timer.
 *
 * A hold point is released by somebody else, on site, while this screen is
 * open. A portfolio still showing work as stopped ten minutes after it was
 * released sends people to ask a question that has already been answered.
 */
export function useHoldPoints() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: keys.holdPoints(site.id),
    queryFn: () => source.listHoldPoints(site.id),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useProjectAttention() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: keys.projectAttention(site.id),
    queryFn: () => source.getProjectAttention(site.id),
    staleTime: 60_000,
  });
}

export function useProjectInspection(id: string) {
  const { source } = useSession();
  return useQuery({ queryKey: keys.projectInspection(id), queryFn: () => source.getProjectInspection(id) });
}

/** Pack content: it changes when a pack is installed, not during a session. */
export function useProjectVocabulary() {
  const { source } = useSession();
  return useQuery({
    queryKey: keys.projectVocabulary(),
    queryFn: () => source.getProjectVocabulary(),
    staleTime: Infinity,
  });
}

export function useTestTypes() {
  const { source } = useSession();
  return useQuery({ queryKey: ["test-types"], queryFn: () => source.listTestTypes(), staleTime: 5 * 60_000 });
}

export function useTestRecords() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["test-records", site.id],
    queryFn: () => source.listTestRecords(site.id),
    staleTime: 30_000,
  });
}

export function useTestingSummary() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["testing-summary", site.id],
    queryFn: () => source.getTestingSummary(site.id),
    staleTime: 30_000,
  });
}

export function useTestRecord(id: string) {
  const { source } = useSession();
  return useQuery({ queryKey: ["test-record", id], queryFn: () => source.getTestRecord(id) });
}

export function useLatestTest(testType: string | undefined) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["latest-test", site.id, testType],
    queryFn: () => source.getLatestTest(site.id, testType!),
    enabled: !!testType,
    staleTime: 60_000,
  });
}

export function useCreateTestRecord() {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewTestInput) => source.createTestRecord(site.id, input),
    onSuccess: () => {
      for (const key of ["test-records", "testing-summary", "latest-test", "estate-summary"])
        void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

const TESTING_KEYS = [
  "test-records",
  "testing-summary",
  "latest-test",
  "estate-summary",
  "test-record",
  "test-history",
  "testing-activity",
  "asset-test-history",
];

export function useReviseTestRecord(recordId: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewTestInput & { reason: string }) => source.reviseTestRecord(recordId, input),
    onSuccess: () => {
      for (const key of TESTING_KEYS) void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export function useTestHistory(recordId: string) {
  const { source } = useSession();
  return useQuery({ queryKey: ["test-history", recordId], queryFn: () => source.getTestHistory(recordId) });
}

export function useAddTestAction(recordId: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { kind: ActionKind; body: string; workOrderId?: string; assetTags?: string[] }) =>
      source.addTestAction(recordId, input),
    onSuccess: () => {
      for (const key of ["test-history", "testing-activity", "test-records", "test-record"])
        void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export function useTestingActivity() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["testing-activity", site.id],
    queryFn: () => source.getTestingActivity(site.id),
    staleTime: 15_000,
  });
}

export function useAssetTestHistory(testType: string, assetTag: string | null) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["asset-test-history", site.id, testType, assetTag],
    queryFn: () => source.getAssetTestHistory(site.id, testType, assetTag!),
    enabled: !!assetTag,
  });
}

export function useUploadTestFile(recordId: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { file: File; note?: string }) => source.uploadTestFile(recordId, v.file, v.note),
    onSuccess: () => {
      for (const key of ["test-record", "test-records", "test-history", "testing-activity"])
        void qc.invalidateQueries({ queryKey: [key] });
    },
  });
}

export function useConsumption(assetTypes: string[], bucket: "day" | "week" | "month", days: number) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["consumption", site.id, assetTypes.join(","), bucket, days],
    queryFn: () => source.getConsumption(site.id, assetTypes, bucket, days),
    staleTime: 5 * 60_000,
  });
}

export function useMeters(assetTypes: readonly string[]) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["meters", site.id, assetTypes.join(",")],
    queryFn: () => source.listMeters(site.id, [...assetTypes]),
    staleTime: 60_000,
  });
}

function invalidateMeters(qc: ReturnType<typeof useQueryClient>) {
  for (const key of ["meters", "consumption", "green-records"]) void qc.invalidateQueries({ queryKey: [key] });
}

export function useRegisterMeter() {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewMeterInput) => source.registerMeter(site.id, input),
    onSuccess: () => invalidateMeters(qc),
  });
}

export function useUploadMeterReadings() {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { csv: string; filename?: string }) => source.uploadMeterReadings(site.id, v.csv, v.filename),
    onSuccess: () => invalidateMeters(qc),
  });
}

export function useEstateSummary(assetTypes: string[]) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["estate-summary", site.id, assetTypes.join(",")],
    queryFn: () => source.getEstateSummary(site.id, assetTypes),
    staleTime: 60_000,
  });
}

export function useNotes(topic: string) {
  const { source, site } = useSession();
  return useQuery({ queryKey: ["notes", site.id, topic], queryFn: () => source.listNotes(site.id, topic) });
}

export function useAddNote(topic: string) {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Omit<NewNoteInput, "topic">) => source.addNote(site.id, { ...input, topic }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["notes", site.id, topic] }),
  });
}

export function useCreateProject() {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProjectInput) => source.createProject(site.id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["projects"] });
      void qc.invalidateQueries({ queryKey: ["project-attention"] });
    },
  });
}

export function useUpdateProject(id: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateProjectInput) => source.updateProject(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.project(id) });
      void qc.invalidateQueries({ queryKey: ["projects"] });
      void qc.invalidateQueries({ queryKey: ["project-attention"] });
    },
  });
}

export function useSetProjectSchedule(id: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ScheduleInput) => source.setProjectSchedule(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.project(id) });
      void qc.invalidateQueries({ queryKey: ["projects"] });
      void qc.invalidateQueries({ queryKey: ["project-attention"] });
    },
  });
}

export function useProjectLog(id: string) {
  const { source } = useSession();
  return useQuery({ queryKey: keys.projectLog(id), queryFn: () => source.getProjectLog(id) });
}

export function useAddProjectLogEntry(id: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NewLogEntryInput) => source.addProjectLogEntry(id, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.projectLog(id) }),
  });
}

export function useCloseProjectAction(projectId: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { entryId: string } & CloseActionInput) =>
      source.closeProjectAction(v.entryId, { outcome: v.outcome, note: v.note }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.projectLog(projectId) }),
  });
}

export function useSubmitGate(projectId: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { gateKey: string } & GateSubmitInput) =>
      source.submitGate(projectId, v.gateKey, { evidence: v.evidence, dueDate: v.dueDate }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.project(projectId) });
      void qc.invalidateQueries({ queryKey: ["project-attention"] });
    },
  });
}

export function useDecideGate(projectId: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { gateKey: string } & GateDecisionInput) =>
      source.decideGate(projectId, v.gateKey, { decision: v.decision, note: v.note, evidence: v.evidence }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.project(projectId) });
      void qc.invalidateQueries({ queryKey: ["projects"] });
      void qc.invalidateQueries({ queryKey: ["project-attention"] });
    },
  });
}

/**
 * Writing to a sheet invalidates the portfolio as well as the sheet.
 *
 * Answering the last unchecked item does not change the inspection alone: it
 * is what makes a hold point releasable, which changes the hold-point list,
 * the "sheets left incomplete" list and the row on the portfolio. Invalidating
 * only the sheet leaves three screens disagreeing with the one in front of you.
 */
function useSheetWrite(inspectionId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: keys.projectInspection(inspectionId) });
    void qc.invalidateQueries({ queryKey: ["project"] });
    void qc.invalidateQueries({ queryKey: ["projects"] });
    void qc.invalidateQueries({ queryKey: ["hold-points"] });
    void qc.invalidateQueries({ queryKey: ["project-attention"] });
  };
}

export function useRecordInspectionItems(inspectionId: string) {
  const { source } = useSession();
  const invalidate = useSheetWrite(inspectionId);
  return useMutation({
    mutationFn: (results: ItemResultInput[]) => source.recordInspectionItems(inspectionId, results),
    onSuccess: invalidate,
  });
}

export function useRecordInspectionResult(inspectionId: string) {
  const { source } = useSession();
  const invalidate = useSheetWrite(inspectionId);
  return useMutation({
    mutationFn: (input: RecordResultInput) => source.recordInspectionResult(inspectionId, input),
    onSuccess: invalidate,
  });
}

export function useReleaseHoldPoint(inspectionId: string) {
  const { source } = useSession();
  const invalidate = useSheetWrite(inspectionId);
  return useMutation({
    mutationFn: (note: string | undefined) => source.releaseHoldPoint(inspectionId, note),
    onSuccess: invalidate,
  });
}

/* ---- Asset Management ------------------------------------------------------------ */

import type {
  AmCreateInput,
  AmFilter,
  AmImportRow,
  AmPatch,
  AmPlanInput,
  AmPolicy,
  NoteCategory,
} from "../domain/assetManagement";

export function useAmRegister(filter: AmFilter) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["am", "register", site.id, filter],
    queryFn: () => source.amRegister(site.id, filter),
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

export function useAmAsset(assetId: string | undefined) {
  const { source } = useSession();
  return useQuery({
    queryKey: ["am", "asset", assetId],
    queryFn: () => source.amAsset(assetId as string),
    enabled: Boolean(assetId),
  });
}

export function useAmCondition() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["am", "condition", site.id],
    queryFn: () => source.amCondition(site.id),
    staleTime: 60_000,
  });
}

export function useAmMaintenance() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["am", "maintenance", site.id],
    queryFn: () => source.amMaintenance(site.id),
    staleTime: 60_000,
  });
}

export function useAmLifecycle() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["am", "lifecycle", site.id],
    queryFn: () => source.amLifecycle(site.id),
    staleTime: 60_000,
  });
}

export function useAmPolicies() {
  const { source } = useSession();
  return useQuery({ queryKey: ["am", "policies"], queryFn: () => source.amPolicies(), staleTime: 300_000 });
}

/** Any Asset Management write changes what every tab shows; refetch them all. */
function useAmInvalidate() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["am"] });
}

export function useAmEditAsset(assetId: string) {
  const { source } = useSession();
  const done = useAmInvalidate();
  return useMutation({ mutationFn: (patch: AmPatch) => source.amEditAsset(assetId, patch), onSuccess: done });
}

export function useAmImport() {
  const { source, site } = useSession();
  const done = useAmInvalidate();
  return useMutation({ mutationFn: (rows: AmImportRow[]) => source.amImport(site.id, rows), onSuccess: done });
}

export function useAmSetPolicy() {
  const { source } = useSession();
  const done = useAmInvalidate();
  return useMutation({
    mutationFn: ({ assetType, policy }: { assetType: string; policy: Omit<AmPolicy, "assetType"> }) =>
      source.amSetPolicy(assetType, policy),
    onSuccess: done,
  });
}

export function useAmRefreshHealth() {
  const { source, site } = useSession();
  const done = useAmInvalidate();
  return useMutation({ mutationFn: () => source.amRefreshHealth(site.id), onSuccess: done });
}

export function useAmCreatePlan() {
  const { source } = useSession();
  const done = useAmInvalidate();
  return useMutation({ mutationFn: (input: AmPlanInput) => source.amCreatePlan(input), onSuccess: done });
}

export function useAmEditPlan() {
  const { source } = useSession();
  const done = useAmInvalidate();
  return useMutation({
    mutationFn: ({
      id,
      patch,
    }: {
      id: string;
      patch: { active?: boolean; intervalDays?: number; nextDueOn?: string | null };
    }) => source.amEditPlan(id, patch),
    onSuccess: done,
  });
}

export function useAmApplyPlan() {
  const { source, site } = useSession();
  const done = useAmInvalidate();
  return useMutation({
    mutationFn: ({ assetType, plan }: { assetType: string; plan: Omit<AmPlanInput, "assetId"> }) =>
      source.amApplyPlan(site.id, assetType, plan),
    onSuccess: done,
  });
}

export function useAmGeneratePlans() {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => source.amGeneratePlans(site.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["am"] });
      void qc.invalidateQueries({ queryKey: ["work-orders"] });
    },
  });
}

export function useAmTypes() {
  const { source } = useSession();
  return useQuery({ queryKey: ["am", "types"], queryFn: () => source.amTypes(), staleTime: 600_000 });
}

export function useAmCreateAsset() {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AmCreateInput) => source.amCreateAsset(site.id, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["am"] }),
  });
}

export function useAmHistory(assetId: string | undefined) {
  const { source } = useSession();
  return useQuery({
    queryKey: ["am", "history", assetId],
    queryFn: () => source.amHistory(assetId as string),
    enabled: Boolean(assetId),
  });
}

export function useAmNotes(assetId: string | undefined) {
  const { source } = useSession();
  return useQuery({
    queryKey: ["am", "notes", assetId],
    queryFn: () => source.amNotes(assetId as string),
    enabled: Boolean(assetId),
  });
}

export function useAmAddNote(assetId: string) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ body, category }: { body: string; category: NoteCategory }) =>
      source.amAddNote(assetId, body, category),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["am", "notes", assetId] });
      void qc.invalidateQueries({ queryKey: ["am", "history", assetId] });
    },
  });
}

/* ---- HR ------------------------------------------------------------------------ */

/*
 * A 403 is an answer, not a fault to retry: pay is closed to anybody without
 * hr:read, and three retries would only delay the screen saying so.
 */
const hrRetry = (count: number, e: Error) =>
  count < 2 && (e as { status?: number }).status !== 403 && !/^Not permitted/.test(e.message);

export function useHrPeople() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["hr", "people", site.id],
    queryFn: () => source.hrPeople(site.id),
    staleTime: 60_000,
    retry: hrRetry,
  });
}

export function useHrRules() {
  const { source } = useSession();
  return useQuery({ queryKey: ["hr", "rules"], queryFn: () => source.hrRules(), staleTime: 300_000, retry: hrRetry });
}

export function useHrCost() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["hr", "cost", site.id],
    queryFn: () => source.hrCost(site.id),
    staleTime: 60_000,
    retry: hrRetry,
  });
}

export function useHrLabour() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["hr", "labour", site.id],
    queryFn: () => source.hrLabour(site.id),
    staleTime: 60_000,
    retry: hrRetry,
  });
}

export function useHrSaudization() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["hr", "saudization", site.id],
    queryFn: () => source.hrSaudization(site.id),
    staleTime: 60_000,
    retry: hrRetry,
  });
}

/** Any HR write moves every figure in the suite: pay, rates, labour, Saudization. */
function useHrInvalidate() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["hr"] });
}

export function useHrAddPerson() {
  const { source, site } = useSession();
  const done = useHrInvalidate();
  return useMutation({ mutationFn: (input: HrEmployeeInput) => source.hrAddPerson(site.id, input), onSuccess: done });
}

export function useHrEditPerson(employeeId: string) {
  const { source } = useSession();
  const done = useHrInvalidate();
  return useMutation({
    mutationFn: (patch: HrEmployeePatch) => source.hrEditPerson(employeeId, patch),
    onSuccess: done,
  });
}

export function useHrSetRules() {
  const { source } = useSession();
  const done = useHrInvalidate();
  return useMutation({ mutationFn: (restated: HrRulesPatch) => source.hrSetRules(restated), onSuccess: done });
}

export function useHrSetOvertime() {
  const { source } = useSession();
  const done = useHrInvalidate();
  return useMutation({
    mutationFn: ({ employeeId, month, hours }: { employeeId: string; month: string; hours: number }) =>
      source.hrSetOvertime(employeeId, month, hours),
    onSuccess: done,
  });
}

export function useHrFile(employeeId: string | undefined) {
  const { source } = useSession();
  return useQuery({
    queryKey: ["hr", "file", employeeId],
    queryFn: () => source.hrFile(employeeId as string),
    enabled: !!employeeId,
    retry: hrRetry,
  });
}

export function useHrRecords(filter?: { kind?: string; status?: string }) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["hr", "records", site.id, filter?.kind ?? "", filter?.status ?? ""],
    queryFn: () => source.hrRecords(site.id, filter),
    staleTime: 30_000,
    retry: hrRetry,
  });
}

export function useHrAttendanceDay(day: string) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["hr", "attendance", site.id, day],
    queryFn: () => source.hrAttendanceDay(site.id, day),
    retry: hrRetry,
  });
}

export function useHrAttendanceRange(from: string, to: string, employeeId?: string) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["hr", "attendance-range", site.id, from, to, employeeId ?? ""],
    queryFn: () => source.hrAttendanceRange(site.id, from, to, employeeId),
    retry: hrRetry,
  });
}

export function useHrAccessView() {
  const { source } = useSession();
  return useQuery({ queryKey: ["hr", "access"], queryFn: () => source.hrAccess(), retry: hrRetry });
}

export function useHrSetPhoto(employeeId: string) {
  const { source } = useSession();
  const done = useHrInvalidate();
  return useMutation({ mutationFn: (dataUrl: string) => source.hrSetPhoto(employeeId, dataUrl), onSuccess: done });
}

export function useHrAddRecord() {
  const { source } = useSession();
  const done = useHrInvalidate();
  return useMutation({ mutationFn: (input: HrRecordInput) => source.hrAddRecord(input), onSuccess: done });
}

export function useHrDecide() {
  const { source } = useSession();
  const done = useHrInvalidate();
  return useMutation({
    mutationFn: ({ id, status, note }: { id: string; status: RecordStatus; note?: string | null }) =>
      source.hrDecide(id, status, note),
    onSuccess: done,
  });
}

export function useHrSetAttendance() {
  const { source } = useSession();
  const done = useHrInvalidate();
  return useMutation({ mutationFn: (input: HrAttendanceInput) => source.hrSetAttendance(input), onSuccess: done });
}

export function useHrSetAccess() {
  const { source } = useSession();
  const done = useHrInvalidate();
  return useMutation({
    mutationFn: ({ principalId, role }: { principalId: string; role: string | null }) =>
      source.hrSetAccess(principalId, role),
    onSuccess: done,
  });
}

export function useHrMe() {
  const { source } = useSession();
  return useQuery({ queryKey: ["hr", "me"], queryFn: () => source.hrMe(), retry: hrRetry });
}

export function useHrClockMe() {
  const { source } = useSession();
  const done = useHrInvalidate();
  return useMutation({ mutationFn: (input: HrClockInput) => source.hrClockMe(input), onSuccess: done });
}

export function useHrSelfRequest() {
  const { source } = useSession();
  const done = useHrInvalidate();
  return useMutation({ mutationFn: (input: HrSelfRequest) => source.hrSelfRequest(input), onSuccess: done });
}

export function useHrGeofences() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["hr", "geofences", site.id],
    queryFn: () => source.hrGeofences(site.id),
    retry: hrRetry,
  });
}

export function useHrAddGeofence() {
  const { source, site } = useSession();
  const done = useHrInvalidate();
  return useMutation({ mutationFn: (f: Omit<HrGeofence, "id">) => source.hrAddGeofence(site.id, f), onSuccess: done });
}

export function useHrEditGeofence() {
  const { source } = useSession();
  const done = useHrInvalidate();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<HrGeofence> }) => source.hrEditGeofence(id, patch),
    onSuccess: done,
  });
}

export function useHrClockEvents(day: string, employeeId?: string) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["hr", "clock-events", site.id, day, employeeId ?? ""],
    queryFn: () => source.hrClockEvents(site.id, day, employeeId),
    retry: hrRetry,
  });
}

export function useHrClockManual() {
  const { source } = useSession();
  const done = useHrInvalidate();
  return useMutation({
    mutationFn: (x: { employeeId: string; direction: "in" | "out"; at: string; note: string }) =>
      source.hrClockManual(x.employeeId, x.direction, x.at, x.note),
    onSuccess: done,
  });
}

export function useHrNotifications(filter?: { employeeId?: string; status?: string }) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["hr", "notifications", site.id, filter?.employeeId ?? "", filter?.status ?? ""],
    queryFn: () => source.hrNotifications(site.id, filter),
    retry: hrRetry,
  });
}

export function useHrSendNow() {
  const { source } = useSession();
  const done = useHrInvalidate();
  return useMutation({ mutationFn: () => source.hrSendNow(), onSuccess: done });
}

export function useHrRetryNotification() {
  const { source } = useSession();
  const done = useHrInvalidate();
  return useMutation({ mutationFn: (id: string) => source.hrRetryNotification(id), onSuccess: done });
}

export function useHrImport() {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (x: {
      rows: { row: number; data: Record<string, unknown>; loginEmail: string | null; leaveBalance: number | null }[];
      dryRun: boolean;
      updateExisting: boolean;
    }) => source.hrImport(site.id, x.rows, { dryRun: x.dryRun, updateExisting: x.updateExisting }),
    onSuccess: (r) => {
      if (!r.dryRun) void qc.invalidateQueries({ queryKey: ["hr"] });
    },
  });
}

/* ── the contact directory, who is told, and what was sent ─────────────────── */

export function useDirectory() {
  const { source, site } = useSession();
  return useQuery({ queryKey: ["contacts", site.id], queryFn: () => source.getDirectory(site.id) });
}

export function useSaveContactOrg() {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (org: Omit<ContactOrg, "people">) => source.saveContactOrg(site.id, org),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["contacts"] }),
  });
}

export function useSaveContactPerson() {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (person: ContactPerson) => source.saveContactPerson(person),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["contacts"] });
      void qc.invalidateQueries({ queryKey: ["reach"] });
    },
  });
}

export function useNotifyRules() {
  const { source, site } = useSession();
  return useQuery({ queryKey: ["notify-rules", site.id], queryFn: () => source.listNotifyRules(site.id) });
}

export function useSaveNotifyRule() {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rule: NotifyRule) => source.saveNotifyRule(rule),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["notify-rules"] }),
  });
}

export function useReach(recipients: Recipient[]) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["reach", site.id, JSON.stringify(recipients)],
    queryFn: () => source.previewRecipients(site.id, recipients),
    enabled: recipients.length > 0,
  });
}

export function useDispatchGroups() {
  const { source, site } = useSession();
  return useQuery({ queryKey: ["dispatch-groups", site.id], queryFn: () => source.listDispatchGroups(site.id) });
}

export function useWorkOrderMessages(workOrderId: string) {
  const { source } = useSession();
  return useQuery({
    queryKey: ["work-order-messages", workOrderId],
    queryFn: () => source.listWorkOrderMessages(workOrderId),
    refetchInterval: 20_000,
  });
}

/* ── the flight record (AODB) ─────────────────────────────────────────────── */

/**
 * How a reader that is always mounted shares a day without adding load:
 * `enabled` holds the read back until it is needed, and `poll: false` reads
 * the shared cache with no timer of its own — the screens that are open keep
 * it fresh.
 */
export interface SharedRead {
  enabled?: boolean;
  poll?: boolean;
}

export function useAodbDay(date?: string, opts: SharedRead = {}) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["aodb", "day", site.id, date ?? "today"],
    queryFn: () => source.aodbDay(site.id, date),
    enabled: opts.enabled ?? true,
    refetchInterval: opts.poll === false ? false : 30_000,
    ...(opts.poll === false ? { staleTime: 60_000 } : {}),
  });
}

export function useAodbSummary(date?: string) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["aodb", "summary", site.id, date ?? "today"],
    queryFn: () => source.aodbSummary(site.id, date),
    refetchInterval: 30_000,
  });
}

export function useAodbMovement(id: string | null) {
  const { source } = useSession();
  return useQuery({ queryKey: ["aodb", "movement", id], queryFn: () => source.aodbMovement(id!), enabled: !!id });
}

export function useAodbChange() {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; changes: Partial<Movement>; version?: number; note?: string }) =>
      source.aodbChange(v.id, v.changes, v.version, v.note),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["aodb"] }),
  });
}

export function useAodbSchedule() {
  const { source, site } = useSession();
  return useQuery({ queryKey: ["aodb", "schedule", site.id], queryFn: () => source.aodbSchedule(site.id) });
}

export function useAodbMessages() {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["aodb", "messages", site.id],
    queryFn: () => source.aodbMessages(site.id),
    refetchInterval: 30_000,
  });
}

export function useAodbPostMessage() {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { kind: "mvt" | "aidx"; raw: string }) => source.aodbPostMessage(site.id, v.kind, v.raw),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["aodb"] }),
  });
}

export function useAodbImport() {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { ssim: string; season?: string }) => source.aodbImportSsim(site.id, v.ssim, v.season),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["aodb"] }),
  });
}

export function useAodbGenerate() {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { from: string; to: string }) => source.aodbGenerate(site.id, v.from, v.to),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["aodb"] }),
  });
}

/* ── flight data feeds ──────────────────────────────────────────────── */

export function useFlightFeeds() {
  const { source, site } = useSession();
  return useQuery({ queryKey: ["feeds", "list", site.id], queryFn: () => source.flightFeeds(site.id), refetchInterval: 30_000 });
}

export function useFlightFeedMessages(opts?: { feedId?: string; outcome?: FeedOutcome }) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["feeds", "messages", site.id, opts?.feedId ?? "", opts?.outcome ?? ""],
    queryFn: () => source.flightFeedMessages(site.id, opts),
    refetchInterval: 30_000,
  });
}

export function useSaveFlightFeed() {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { draft: FeedDraft; id?: string }) => source.saveFlightFeed(site.id, v.draft, v.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["feeds"] }),
  });
}

export function useTestFlightFeed() {
  const { source } = useSession();
  return useMutation({ mutationFn: (v: { id: string; sample?: string }) => source.testFlightFeed(v.id, v.sample) });
}

export function useReplayFeedMessage() {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => source.replayFeedMessage(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["feeds"] });
      void qc.invalidateQueries({ queryKey: ["aodb"] });
    },
  });
}

/* ── resource allocation ────────────────────────────────────────────── */

export function useAllocDay(opts: SharedRead = {}) {
  const { source, site } = useSession();
  return useQuery({
    queryKey: ["alloc", "day", site.id],
    queryFn: () => source.allocDay(site.id),
    enabled: opts.enabled ?? true,
    refetchInterval: opts.poll === false ? false : 60_000,
    ...(opts.poll === false ? { staleTime: 60_000 } : {}),
  });
}

function useAllocMutation<V, R>(fn: (source: DataSource, siteId: string, v: V) => Promise<R>) {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: V) => fn(source, site.id, v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["alloc"] }),
  });
}

export const useAllocReplan = () =>
  useAllocMutation((s, id, mode: "replan" | "plan") => s.allocReplan(id, mode));
export const useAllocDecide = () =>
  useAllocMutation((s, id, v: { proposalId: string; decision: "apply" | "reject"; note?: string }) =>
    s.allocDecide(id, v.proposalId, v.decision, v.note),
  );
export const useAllocAssign = () =>
  useAllocMutation((s, id, v: { key: string; codes: string[]; note?: string; force?: boolean }) =>
    s.allocAssign(id, v.key, v.codes, { note: v.note, force: v.force }),
  );
export const useAllocPolicy = () => useAllocMutation((s, id, p: Policy) => s.allocSetPolicy(id, p));
export const useAllocSaveRule = () => useAllocMutation((s, id, r: Rule) => s.allocSaveRule(id, r));
export const useAllocDeleteRule = () => useAllocMutation((s, id, ruleId: string) => s.allocDeleteRule(id, ruleId));

/*
 * The writes the flight screens make: a delay notice moves times and so the
 * stand plan; a release or an added stand changes the plan the screens read.
 * Each refreshes both the flight record and the allocation.
 */
function useFlightWorldMutation<V, R>(fn: (source: DataSource, siteId: string, v: V) => Promise<R>) {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: V) => fn(source, site.id, v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["aodb"] });
      void qc.invalidateQueries({ queryKey: ["alloc"] });
    },
  });
}

export const useAodbNotice = () =>
  useFlightWorldMutation(
    (s, id, v: { movementId: string; source: string; newTime: string; code?: string; note?: string; version?: number }) =>
      s.aodbNotice(id, v.movementId, { source: v.source, newTime: v.newTime, code: v.code, note: v.note }, v.version),
  );
export const useAllocRelease = () =>
  useFlightWorldMutation((s, id, v: { key: string; note?: string }) => s.allocRelease(id, v.key, v.note));
export const useAllocAddResource = () => useFlightWorldMutation((s, id, item: Resource) => s.allocAddResource(id, item));

/* ── build 6: the flight-record figures a person enters by hand ──
 * Each moves what the flight screens read (times, loads, the stand plan's
 * occupancy), so each refreshes the record and the plan. */
export const useAodbAdd = () => useFlightWorldMutation((s, id, d: NewMovement) => s.aodbAdd(id, d));
/*
 * An entry answers with the movement as it now is. It goes straight into the
 * panel's copy, so the next entry carries the new version even before the
 * refresh lands; without that, a second entry typed quickly is refused as
 * made against an old version.
 */
type EntryAnswer = { movement: Movement; changed: string[]; replaced: { field: string; source: string }[] };
function useAodbEntry<V extends { movementId: string }>(fn: (source: DataSource, v: V) => Promise<EntryAnswer>) {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: V) => fn(source, v),
    onSuccess: (out, v) => {
      qc.setQueryData(["aodb", "movement", v.movementId], (old: unknown) =>
        old && typeof old === "object" ? { ...(old as object), movement: out.movement } : old,
      );
      void qc.invalidateQueries({ queryKey: ["aodb"] });
      void qc.invalidateQueries({ queryKey: ["alloc"] });
    },
  });
}

export const useAodbActuals = () =>
  useAodbEntry((s, v: { movementId: string; times: Partial<Record<ActualKey, string>>; version?: number; note?: string }) =>
    s.aodbActuals(v.movementId, v.times, v.version, v.note),
  );
export const useAodbLoads = () =>
  useAodbEntry(
    (
      s,
      v: {
        movementId: string;
        loads: Partial<Record<string, number | null>>;
        capacity?: { value: number | null };
        version?: number;
        note?: string;
      },
    ) =>
      s.aodbLoads(v.movementId, v.loads, {
        ...(v.capacity ? { capacity: v.capacity.value } : {}),
        version: v.version,
        note: v.note,
      }),
  );
export const useAodbDelayCodes = () =>
  useAodbEntry((s, v: { movementId: string; codes: { code: string; minutes?: number }[]; version?: number; note?: string }) =>
    s.aodbDelayCodes(v.movementId, v.codes, v.version, v.note),
  );
export const useAodbTobt = () =>
  useAodbEntry((s, v: { movementId: string; tobt: string; givenBy?: string; version?: number; note?: string }) =>
    s.aodbTobt(v.movementId, v.tobt, { givenBy: v.givenBy, version: v.version, note: v.note }),
  );

export function useAllocRegister() {
  const { source, site } = useSession();
  return useQuery({ queryKey: ["alloc", "register", site.id], queryFn: () => source.allocRegister(site.id) });
}
export const useAllocChangeResource = () =>
  useFlightWorldMutation(
    (s, id, v: { code: string; changes: Partial<Resource> & { active?: boolean }; note?: string }) =>
      s.allocChangeResource(id, v.code, v.changes, v.note),
  );

export function useAllocWhatIf() {
  const { source, site } = useSession();
  return useMutation({ mutationFn: (change: WhatIf) => source.allocWhatIf(site.id, change) });
}

export function useAllocWindows() {
  const { source, site } = useSession();
  return useMutation({
    mutationFn: (v: { code: string; minutes: number; hours?: number }) => source.allocWindows(site.id, v.code, v.minutes, v.hours),
  });
}

/* ── passenger flow ─────────────────────────────────────────────────── */

export function useFlowDay() {
  const { source, site } = useSession();
  return useQuery({ queryKey: ["flow", "day", site.id], queryFn: () => source.flowDay(site.id), refetchInterval: 60_000 });
}

function useFlowMutation<V, R>(fn: (source: DataSource, siteId: string, v: V) => Promise<R>) {
  const { source, site } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: V) => fn(source, site.id, v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["flow"] }),
  });
}

export const useFlowObserve = () =>
  useFlowMutation((s, id, o: Omit<Observation, "source" | "by">) => s.flowObserve(id, o));
export const useFlowAdopt = () =>
  useFlowMutation((s, id, v: { code: string; steps: LanePlan["steps"]; note?: string }) => s.flowAdopt(id, v.code, v.steps, v.note));
export const useFlowCheckpoint = () => useFlowMutation((s, id, cp: Checkpoint) => s.flowSaveCheckpoint(id, cp));

/* ── staff calls ─────────────────────────────────────────────────────────
 * Not keyed by site: calls are the tenant's, and a person rings a colleague
 * whichever aerodrome either of them has on screen. */

export function useCallDirectory(enabled = true) {
  const { source } = useSession();
  return useQuery({
    queryKey: ["calls-directory"],
    queryFn: () => source.callsDirectory(),
    enabled,
    /* presence moves: who is online is read again while the screen is open */
    refetchInterval: 30_000,
    retry: (n, e) => (e as { status?: number }).status !== 403 && n < 2,
  });
}

export function useCallHistory(enabled = true) {
  const { source } = useSession();
  return useQuery({
    queryKey: ["calls-history"],
    queryFn: () => source.callsHistory(50),
    enabled,
    refetchInterval: 60_000,
    retry: (n, e) => (e as { status?: number }).status !== 403 && n < 2,
  });
}

export function useCallIce(enabled = true) {
  const { source } = useSession();
  return useQuery({ queryKey: ["calls-ice"], queryFn: () => source.callsIce(), enabled, staleTime: 10 * 60_000 });
}

export function useCallPhoto(employeeId: string | undefined, hasPhoto: boolean) {
  const { source } = useSession();
  return useQuery({
    queryKey: ["calls-photo", employeeId],
    queryFn: async () => (await source.callsPhoto(employeeId!)) ?? null,
    enabled: !!employeeId && hasPhoto,
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export function useCallPolicy(enabled = true) {
  const { source } = useSession();
  return useQuery({ queryKey: ["calls-policy"], queryFn: () => source.callsPolicy(), enabled });
}

export function useSaveCallPolicy() {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    /* build 5: `basedOn` is the save the editor started from, so a colleague's later save is not replaced */
    mutationFn: (input: CallPolicy | { policy: CallPolicy; basedOn?: string }) =>
      "matrix" in input ? source.saveCallsPolicy(input) : source.saveCallsPolicy(input.policy, input.basedOn),
    onSuccess: (view) => {
      qc.setQueryData(["calls-policy"], view);
      /* who may be called, and by video, follows the policy */
      void qc.invalidateQueries({ queryKey: ["calls-directory"] });
    },
  });
}

export function useCheckCallPolicy() {
  const { source } = useSession();
  return useMutation({ mutationFn: (p: { from: string; to: string }) => source.checkCallsPolicy(p.from, p.to) });
}

/* staff calls v2: do not disturb, live calls, meetings */

const notOnCalls = (n: number, e: unknown) => (e as { status?: number }).status !== 403 && n < 2;

export function useCallDnd(enabled = true) {
  const { source } = useSession();
  return useQuery({ queryKey: ["calls-dnd"], queryFn: () => source.callsDnd(), enabled, retry: notOnCalls });
}

export function useSetCallDnd() {
  const { source } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { until: string | "forever" | null; allow: string[] }) => source.callsSetDnd(v.until, v.allow),
    onSuccess: (d: DndState) => qc.setQueryData(["calls-dnd"], d),
  });
}

export function useLiveCalls(enabled = true) {
  const { source } = useSession();
  return useQuery({
    queryKey: ["calls-live"],
    queryFn: () => source.callsLive(),
    enabled,
    refetchInterval: 15_000,
    retry: notOnCalls,
  });
}

export function useCallMeetings(scope: "upcoming" | "past", enabled = true) {
  const { source } = useSession();
  return useQuery({
    queryKey: ["calls-meetings", scope],
    queryFn: () => source.callsMeetings(scope),
    enabled,
    refetchInterval: scope === "upcoming" ? 30_000 : false,
    retry: notOnCalls,
  });
}

function useMeetingsChanged() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["calls-meetings"] });
    void qc.invalidateQueries({ queryKey: ["calls-live"] });
  };
}

export function useSaveMeeting() {
  const { source } = useSession();
  const changed = useMeetingsChanged();
  return useMutation({
    mutationFn: (v: { draft: MeetingDraft; id?: string }) => source.callsSaveMeeting(v.draft, v.id),
    onSuccess: changed,
  });
}

export function useCancelMeeting() {
  const { source } = useSession();
  const changed = useMeetingsChanged();
  return useMutation({ mutationFn: (id: string) => source.callsCancelMeeting(id), onSuccess: changed });
}
